import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Divider,
  Grid, Stack, TextField, Typography,
} from '@mui/material';
import Page from '../Page';
import PageHeader from './shared/PageHeader';
import { getMySignature, updateMySignature, previewMySignature } from '../../utils/outreachApi';
import { showErrorToast, showSuccessToast } from '../../utils/Toast/SnackbarProvider';

const FIELDS = [
  { key: 'full_name',     label: 'Full Name',     helper: 'Shown in bold at the top of the signature.', required: true },
  { key: 'designation',   label: 'Designation',   helper: 'Italic line under your name. e.g. "Partner".' },
  { key: 'phone',         label: 'Office Phone',  helper: 'Hidden if blank.' },
  // Mobile is split across two side-by-side fields: country code + number.
  // Renders as one "row" via the layout below.
  { key: 'mobile',                 label: 'Mobile',                 helper: 'Hidden if blank.',                              pairWith: 'mobile_country_code' },
  { key: 'mobile_country_code',    label: 'Country code',           helper: 'e.g. "+91". Combined with the number above.',  hideStandalone: true },
  { key: 'teams_handle',  label: 'Teams Handle',  helper: 'Hidden if blank.' },
  { key: 'linkedin_url',  label: 'LinkedIn URL',  helper: 'Full https:// URL. Hidden if blank.' },
  { key: 'address_line1', label: 'Address line 1', helper: 'Defaults to "8th Floor Corporate House".' },
  { key: 'address_line2', label: 'Address line 2', helper: 'Defaults to "Bodakdev".' },
  { key: 'address_line3', label: 'Address line 3', helper: 'Defaults to "Ahmedabad - 380054".' },
];

const emptyProfile = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: '' }), { is_configured: false });

const SignaturePage = () => {
  const [profile, setProfile]         = useState(emptyProfile);
  const [savedProfile, setSaved]      = useState(emptyProfile);   // last server state, for dirty check
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [previewing, setPreviewing]   = useState(false);
  const debounceRef                   = useRef(null);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data } = await getMySignature();
      const next = { ...emptyProfile, ...(data?.profile || {}) };
      setProfile(next);
      setSaved(next);
      setPreviewHtml(data?.signature_html || '');
    } catch (err) {
      showErrorToast(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  // Debounced live preview: 450 ms after the last edit, fetch a fresh render
  // with the unsaved overrides merged.
  useEffect(() => {
    if (loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setPreviewing(true);
      try {
        const payload = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: profile[f.key] || '' }), {});
        const { data } = await previewMySignature(payload);
        setPreviewHtml(data?.signature_html || '');
      } catch (err) {
        // Silent — preview is best-effort; keep showing the prior render.
      } finally {
        setPreviewing(false);
      }
    }, 450);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(profile), loading]);

  const isDirty = FIELDS.some((f) => (profile[f.key] || '') !== (savedProfile[f.key] || ''));
  const nameMissing = !(profile.full_name || '').trim();

  const onField = (key) => (e) => setProfile((p) => ({ ...p, [key]: e.target.value }));

  const onSave = async () => {
    if (nameMissing) {
      showErrorToast('Full Name is required.');
      return;
    }
    setSaving(true);
    try {
      const payload = FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: profile[f.key] || '' }), {});
      const { data } = await updateMySignature(payload);
      const next = { ...emptyProfile, ...(data?.profile || {}) };
      setProfile(next);
      setSaved(next);
      setPreviewHtml(data?.signature_html || '');
      showSuccessToast('Signature saved.');
    } catch (err) {
      showErrorToast(err);
    } finally {
      setSaving(false);
    }
  };

  const onReset = () => setProfile(savedProfile);

  return (
    <Page title="Signature">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <PageHeader
          title="Email signature"
          subtitle="Customise the signature appended to every outreach email you send."
          actions={(
            <Stack direction="row" spacing={1.5}>
              <Button onClick={onReset} disabled={!isDirty || saving}>Reset</Button>
              <Button
                variant="contained"
                onClick={onSave}
                disabled={!isDirty || saving || nameMissing}
              >
                {saving ? 'Saving…' : 'Save signature'}
              </Button>
            </Stack>
          )}
        />

        {!loading && !savedProfile.is_configured && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You haven't saved a signature yet. The preview below uses defaults — fill in your details
            and click <strong>Save signature</strong> so outgoing campaign emails show the right info.
          </Alert>
        )}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Form */}
            <Grid item xs={12} md={5}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Your details
                  </Typography>
                  <Stack spacing={2}>
                    {FIELDS.map((f) => {
                      // Skip side-by-side children (rendered by their pair owner).
                      if (f.hideStandalone) return null;

                      // Fields that pair with another (e.g. mobile + country code)
                      // render as a side-by-side row.
                      if (f.pairWith) {
                        const pair = FIELDS.find((x) => x.key === f.pairWith);
                        return (
                          <Stack key={f.key} direction="row" spacing={1.5} alignItems="flex-start">
                            {/* Country code first, narrow */}
                            <TextField
                              label={pair.label}
                              value={profile[pair.key] || ''}
                              onChange={onField(pair.key)}
                              helperText={pair.helper}
                              size="small"
                              sx={{ width: 130 }}
                              placeholder="+91"
                            />
                            {/* Then the number, fills remaining space */}
                            <TextField
                              label={f.label + (f.required ? ' *' : '')}
                              value={profile[f.key] || ''}
                              onChange={onField(f.key)}
                              helperText={f.helper}
                              size="small"
                              sx={{ flex: 1 }}
                            />
                          </Stack>
                        );
                      }
                      return (
                        <TextField
                          key={f.key}
                          label={f.label + (f.required ? ' *' : '')}
                          value={profile[f.key] || ''}
                          onChange={onField(f.key)}
                          helperText={f.helper}
                          fullWidth
                          size="small"
                          error={f.required && nameMissing && f.key === 'full_name'}
                        />
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>

            {/* Live preview */}
            <Grid item xs={12} md={7}>
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Live preview
                    </Typography>
                    {previewing && <CircularProgress size={16} />}
                  </Stack>
                  <Divider sx={{ mb: 2 }} />
                  {previewHtml ? (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        overflowX: 'auto',
                        background: '#fff',
                      }}
                      // The signature is server-rendered HTML; safe because the
                      // backend escapes every dynamic field via _esc().
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: previewHtml }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Enter your full name to see the preview.
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    The brand chrome (logo, color bars, Gateway Group footer) is fixed and not editable.
                    Empty contact fields (phone, mobile, Teams, LinkedIn) are hidden in the rendered signature.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Page>
  );
};

export default SignaturePage;
